/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useRef, useCallback, useEffect } from 'react';
import _, { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { DocumentTitle } from '../../../components/shared/DocumentTitle';
import { Loader } from '../../../components/shared/Loader';
import { useCloseAlertMutation } from '../../../components/weekly-recap/useCloseAlertMutation';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useLoading } from '../../../contexts/LoadingContext';
import { useSignalRContext } from '../../../contexts/SignalRContext';
import usePerformanceNavigation, { EntryTypeEnum } from '../../../hooks/usePerformanceNavigation';
import { IEntityLease } from '../../../interfaces/IEntityLease';
import { IValidation } from '../../../interfaces/IError';
import { ILookupModel } from '../../../interfaces/ILookup';
import { IOrganizationSettingModel } from '../../../interfaces/IOrganization';
import {
    IReportModel,
    IReportBaseModel,
    IReportUserModel,
    IReportSaveModel,
    IUpdateAlertUserModel,
    IExportSettings,
} from '../../../interfaces/Report/IReport';
import {
    ITimelineReportInputModel,
    IReportSectionDetailModel,
    IWeeklyRecapModel,
    IMonthlyRecapModel,
} from '../../../interfaces/Report/ITimelineReport';
import { useObtainEntityLeaseMutation } from '../../../shared/react-query-hooks/mutations/useObtainEntityLeaseMutation';
import { useReleaseEntityLeaseMutation } from '../../../shared/react-query-hooks/mutations/useReleaseEntityLeaseMutation';
import { useGetDbAppSettings } from '../../../shared/react-query-hooks/useGetDbAppSettings';
import { useGetNoteCategoryTypes } from '../../../shared/react-query-hooks/useGetNoteCategoryTypes';
import { useGetOrganizationSettings } from '../../../shared/react-query-hooks/useGetOrganizationSettings';
import { useGetIsEntityLeaseAvailable } from '../../../shared/react-query-hooks/userGetIsEntityLeaseAvailable';
import { useZoneUsers } from '../../../shared/react-query-hooks/useZoneUsers';
import { UnsavedChangesBlocker } from '../../../shared/UnsavedChangesBlocker';
import ValidateUtils from '../../../shared/validations';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import {
    TimelineReportSections,
    DataScopesEnum,
    EntityTypes,
    DatabaseAppSettingsEnum,
    AlertTypesEnum,
    AlertSubTypesEnum,
    ReportUserTypesEnum,
    ReportStatusTypesEnum,
    SystemGeneratedDocumentsTypesEnum,
    UserRightsEnum,
    CollabReportPageActionsType,
} from '../../../utilities/Constants';
import { DisplayMessages } from '../../../utilities/DisplayMessages';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { IOpenChatActModel, SignalRMethods } from '../../../utilities/SignalR';
import UIHelper from '../../../utilities/UIHelper';
import { MonthlyRecapReportRenderer } from '../timeline/monthly-recap/MonthlyRecapReportRenderer';
import { WeeklyRecapReportRenderer } from '../timeline/weekly-recap/WeeklyRecapReportRenderer';
import { CollabReportPageActions } from './CollabReportPageActions';
import { CollabReportPageHeader } from './CollabReportPageHeader';
import { CollabReportDisplayMode } from './enums/CollabReportDisplayMode';
import { CollabReportUnsavedChangesReason } from './enums/CollabReportUnsavedChangesReason';
import { useCollabReportModal, CollabModalTypes } from './modals/CollabModalContext';
import { IChatModalData } from './modals/ReportChatModal';
import { ISaveReportModalData } from './modals/SaveReportModal';
import { IKpiReportInputModel, IKpiReportModel } from '../kpi-report/KpiReport';
import { KpiReportRenderer } from '../kpi-report/KpiReportRenderer';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import { ICaseModel } from '../../../interfaces/ICase';
import { usePingEntityLeaseMutation } from '../../../shared/react-query-hooks/mutations/usePingEntityLeaseMutation';
import { IAlreadyEditingModalData } from './modals/AlreadyEditingModal';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { Stack } from 'react-bootstrap';
import { NoteEditor } from '../../../components/notes/note-editor/NoteEditor';
import Authorization from '../../../stores/Authorization';
import NoteHelper from '../../../utilities/NoteHelper';
import { INoteModel } from '../../../interfaces/INote';
import { ISaveReportNoticeModalData } from './modals/SaveReportNoticeModal';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { MainContentOverlayComponent } from '../../../components/shared/MainContentOverlay';
import CaseEasyUpdate from '../../../components/case/easy-update/CaseEasyUpdate';
import { DismissableModal } from '../../../components/shared/DismissableModal';
import { showErrorToast, showInfoToast } from '../../../shared/ToastUtils';
import { RenderFailedToObtainLeaseModal } from './modals/FailedToObtainLeaseModal';
import { DocumentTypeEnum } from '../../../utilities/DbConstants';

export type CollabReportTypes = ITimelineReportInputModel | IKpiReportInputModel;

const LeaseDuration = 60 * 60 * 8 * 1000;
const PingLeasefrequencyMs = 30 * 1000;

const getReportDifferences = (reportA?: IReportModel, reportB?: IReportModel) => {
    // ignore the following om baseDetails when checking for differences: loggedInUserHasOpenChatAct, loggedInUserHasOpenCollabAct, loggedInUserHasOpenReviewAct

    if (!reportA || !reportB) {
        return {};
    }

    const {
        loggedInUserHasOpenChatAct: a,
        loggedInUserHasOpenCollabAct: b,
        loggedInUserHasOpenReviewAct: c,
        ...otherReportABaseDetails
    } = reportA.baseDetails;

    const modifiedbReportA: IReportModel = {
        ...reportA,
        baseDetails: otherReportABaseDetails,
    };

    const {
        loggedInUserHasOpenChatAct: x,
        loggedInUserHasOpenCollabAct: y,
        loggedInUserHasOpenReviewAct: z,
        ...otherReportBBaseDetails
    } = reportB.baseDetails;

    const modifiedReportB: IReportModel = {
        ...reportB,
        baseDetails: otherReportBBaseDetails,
    };

    return !_.isEmpty(modifiedbReportA) && !_.isEmpty(modifiedReportB)
        ? UIHelper.difference(_.cloneDeep(modifiedbReportA), _.cloneDeep(modifiedReportB))
        : {};
};

const getInputModelDifferences = (
    inputModelA: CollabReportTypes,
    inputModelB: CollabReportTypes,
    isCollabReport: boolean
) => {
    if (isCollabReport) {
        return UIHelper.difference(_.cloneDeep(inputModelA), _.cloneDeep(inputModelB));
    }

    return UIHelper.difference(_.cloneDeep(inputModelA), _.cloneDeep(inputModelB));
};

export interface ICollabReportState {
    currentNote?: INoteModel;
    easyUpdateAttemptedCaseList: string[];
    easyUpdateMode: boolean;
    easyUpdateCaseGuid?: string | undefined;
    noteEditAttemptedGuidList: string[];
    orgSettings: IOrganizationSettingModel[];
    selectAllSections: TimelineReportSections[];
    suppressSaveNotice?: boolean;
    validation?: IValidation;
}

export interface ICollabReportViewProps<T, I> {
    data: T;
    collabReportState: ICollabReportState;
    isPreviewMode: boolean;
    isReportReadonly: boolean;
    inputModel: I;
    reportGuid?: string;
    title: string;
    onEventTriggered: (event: string, data?: any) => void;
    onSectionCheckedChange: (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections
    ) => void;
    show?: boolean;
    setCollabState: (setter: (prevState: ICollabReportState) => ICollabReportState) => void;
}

export interface IControllerProps<T, I> {
    collabReportUrl: string;
    data: T | undefined;
    exportSettings: IExportSettings;
    getReportData?: () => Promise<void>;
    inputModel: I;
    onCollabReportLoaded?: (data: T | undefined) => void;
    onInputModelChange: (newInputModel: I) => void;
    onShowCommentsModal: (show: boolean, section: number) => void;
    onShowSettingsModal: (show: boolean) => void;
    onReportStatusChange: (status: ILookupModel) => void;
    pageTitle: string;
    reportTypeId: number;
    sections?: ILookupModel[];
    timelineReportTypeId?: number;
}

function CollabReportController<T, I extends CollabReportTypes>(props: IControllerProps<T, I>) {
    const { onReportStatusChange } = props;

    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const { guid: reportGuid } = useParams();
    const loading = useLoading();
    const { alertHub } = useSignalRContext();
    const closeChatActMutation = useCloseAlertMutation();
    const navigationType = usePerformanceNavigation();
    const pingMutation = usePingEntityLeaseMutation();
    const modal = useCollabReportModal();

    const filterRights: number[] = [UserRightsEnum.Reports];
    const zoneUsers = useZoneUsers(
        zoneContext.zoneApiClient,
        DataScopesEnum.National.Value,
        filterRights
    );

    const orgSettings = useGetOrganizationSettings();
    const noteCategoryTypes = useGetNoteCategoryTypes();
    const appSettings = useGetDbAppSettings();

    const obtainEntityLeaseMutation = useObtainEntityLeaseMutation();
    const releaseEntityLeaseMutation = useReleaseEntityLeaseMutation();
    const getIsEntityLeaseAvailable = useGetIsEntityLeaseAvailable(
        EntityTypes.Report,
        reportGuid,
        !!navigationType && !!reportGuid
    );

    const [collabState, setCollabState] = useState<ICollabReportState>({
        easyUpdateAttemptedCaseList: [],
        easyUpdateMode: false,
        noteEditAttemptedGuidList: [],
        orgSettings: [],
        selectAllSections: [],
        suppressSaveNotice: false,
    });

    const [originalReportState, setOriginalReportState] = useState<{
        collaborationReport: IReportModel;
        inputModel: I;
    }>({ inputModel: props.inputModel } as {
        collaborationReport: IReportModel;
        inputModel: I;
    });

    const [validation, setValidation] = useState<IValidation>({});
    const [collaborationReport, setCollaborationReport] = useState<IReportModel>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
    const [forceHideEditButton, setForceHideEditButton] = useState<boolean>(false);
    const [displayMode, setDisplayMode] = useState<CollabReportDisplayMode>(
        !reportGuid ? CollabReportDisplayMode.Create : CollabReportDisplayMode.Readonly
    );
    const [refreshDataDialogState, setRefreshDataDialogState] = useState<{
        show: boolean;
        hasBeenDismissed: boolean;
    }>({ show: false, hasBeenDismissed: false });

    const [existingLease, setExistingLease] = useState<IEntityLease | undefined>();
    const existingLeaseRef = useRef<IEntityLease | undefined>(undefined); // used to store the existingLease so it's available when unmounting

    const reportDifferences = getReportDifferences(
        originalReportState?.collaborationReport,
        collaborationReport
    );

    const inputModelDifferences = getInputModelDifferences(
        originalReportState?.inputModel,
        props.inputModel,
        !!reportGuid
    );

    const hasUnsavedChanges =
        !isRedirecting && (!_.isEmpty(reportDifferences) || !_.isEmpty(inputModelDifferences));

    const getCollaborationReport = async (reportGuid: string) => {
        const response = await zoneContext.zoneApiClient.getCollaborationReport(reportGuid);
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }

        if (response.httpResponse.status === 403) {
            zoneContext.zoneNavigate(LocalRoutes.AccessDenied, { replace: true });
            return;
        }

        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            setIsLoading(false);
            return;
        }

        return response.payload;
    };

    const loadCollabReport = async () => {
        const validation: IValidation = {};
        setDisplayMode(CollabReportDisplayMode.Readonly);

        setIsLoading(true);

        if (!orgSettings.isError || noteCategoryTypes.isError || zoneUsers.isError) {
            validation.model = [DisplayMessages.UnexpectedError];
        }

        let collaborationReportCopy = cloneDeep(collaborationReport);
        let inputModelCopy = cloneDeep(props.inputModel);
        let reportDataCopy = cloneDeep(props.data);

        if (reportGuid) {
            const serverCollabReport = await getCollaborationReport(reportGuid);
            if (serverCollabReport?.baseDetails.guid) {
                collaborationReportCopy = serverCollabReport;

                if (serverCollabReport.reportData?.reportParameterJson) {
                    inputModelCopy = JSON.parse(serverCollabReport.reportData?.reportParameterJson);
                    inputModelCopy.entityGuid = reportGuid;
                    //todo:aj - verify after feature is implemented.
                    inputModelCopy.saveReport = true;
                    if (serverCollabReport.reportData?.data) {
                        reportDataCopy = JSON.parse(serverCollabReport.reportData?.data);
                    }
                    props.onShowSettingsModal(false);
                }
            }
        }

        setCollaborationReport(collaborationReportCopy);
        if (collaborationReportCopy) {
            setOriginalReportState({
                collaborationReport: collaborationReportCopy,
                inputModel: inputModelCopy,
            });
        }

        props.onInputModelChange(inputModelCopy);

        if (props.onCollabReportLoaded) {
            props.onCollabReportLoaded(reportDataCopy);
        }

        setIsLoading(false);
    };

    const getSectionsAfterSectionCheckedChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections,
        sections: IReportSectionDetailModel[] = []
    ) => {
        const sectionsCopy = cloneDeep(sections);
        const isChecked = event.target.checked;
        const found = sectionsCopy.find((x) => x.section === section);

        if (found) {
            found.excludeInReport = !isChecked;
        } else if (isChecked) {
            sectionsCopy?.push({
                section,
                sectionName: section.toString(),
                excludeInReport: false,
            });
        }
        return sectionsCopy?.filter((detail) => !detail.excludeInReport);
    };

    const getLeaseDuration = useCallback((): number => {
        const leaseDurationSetting = appSettings.data?.find(
            (setting) => setting.name === DatabaseAppSettingsEnum.CollabReportLeaseDuration
        )?.value;

        const durationAsNumber = parseInt(leaseDurationSetting ?? '');

        if (isNaN(durationAsNumber)) {
            return LeaseDuration;
        }

        return durationAsNumber;
    }, [appSettings.data]);

    const getPageActionsDisplayType = () => {
        if (displayMode === CollabReportDisplayMode.Create && props.data) {
            return CollabReportPageActionsType.CollabReportCreateActions;
        }

        if (displayMode === CollabReportDisplayMode.Edit && collaborationReport) {
            return CollabReportPageActionsType.CollabReportNonReadOnlyActions;
        }

        if (!props.data) {
            return CollabReportPageActionsType.SettingsOnly;
        }

        return CollabReportPageActionsType.CollabReportReadOnlyActions;
    };

    const getLeasePingFrequency = useCallback((): number => {
        const pingFrequencySetting = appSettings.data?.find(
            (setting) => setting.name === DatabaseAppSettingsEnum.EntityLeasePingIntervalInSeconds
        )?.value;
        const frequencyAsNumber = parseInt(pingFrequencySetting ?? '');
        if (isNaN(frequencyAsNumber)) {
            return PingLeasefrequencyMs;
        }
        return frequencyAsNumber * 1000; // convert to MS
    }, [appSettings.data]);

    const doesCollabReportRequireAtleast1Section = (type: number): boolean => {
        if (
            type === SystemGeneratedDocumentsTypesEnum.WeeklyRecap.Value ||
            type === SystemGeneratedDocumentsTypesEnum.MonthlyRecap.Value
        )
            return true;
        return false;
    };

    const saveReport = async (
        report: IReportBaseModel,
        collaborators: IReportUserModel[],
        readers: IReportUserModel[],
        redirectOnSave?: boolean
    ) => {
        const hasZeroSections = (props.inputModel.reportSectionDetails?.length ?? 0) === 0;

        if (hasZeroSections && doesCollabReportRequireAtleast1Section(report.type?.id ?? 0)) {
            setValidation((prev) => ({ ...prev, settings: ['Select at least 1 section'] }));
            return;
        } else {
            setValidation((prev) => ({ ...prev, settings: [] }));
        }

        const request: IReportSaveModel = {
            baseDetails: { ...report, type: { id: props.reportTypeId } },
            entityLeaseGuid: existingLeaseRef.current?.guid,
            users: collaborators,
            readers,
            reportParameterJson: JSON.stringify(props.inputModel),
        };

        setIsSaving(true);
        setIsRedirecting(redirectOnSave ?? false);

        const response = await zoneContext.zoneApiClient.saveCollaborationReport(request);

        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }

        if (!response.httpResponse.ok) {
            showErrorToast(`Failed to save Report - ${collaborationReport?.baseDetails.name}`);
            setIsSaving(false);
            return;
        } else {
            setIsSaving(false);
            const newReportState = response.payload!;
            setCollaborationReport(newReportState);
            setOriginalReportState({
                collaborationReport: newReportState,
                inputModel: props.inputModel,
            });
            setDisplayMode(CollabReportDisplayMode.Readonly);
            setExistingLease(undefined);
            setForceHideEditButton(false);

            if (redirectOnSave && props.collabReportUrl) {
                setIsRedirecting(false);
                zoneContext.zoneNavigate(
                    props.collabReportUrl.replace(':guid', response.payload?.baseDetails.guid ?? '')
                );
            }

            return response.payload;
        }
    };

    const forceCancelLease = useCallback(
        (leaseGuid: string, onSuccess?: () => void, onError?: () => void) => {
            if (!reportGuid) return;

            releaseEntityLeaseMutation
                .mutateAsync(leaseGuid)
                .then(() => {
                    existingLeaseRef.current = undefined;
                    setExistingLease(undefined);
                    setForceHideEditButton(false);
                    modal.closeModal(CollabModalTypes.AlreadyEditing);

                    if (onSuccess) {
                        onSuccess();
                    }
                })
                .catch(() => {
                    showErrorToast(
                        `Failed to release entity lease for report - ${collaborationReport?.baseDetails.name}`
                    );

                    if (onError) {
                        onError();
                    }
                });
        },
        [modal, releaseEntityLeaseMutation, reportGuid]
    );

    const cancelLease = (readonly: boolean) => {
        if (readonly || !existingLeaseRef.current?.guid) return;

        forceCancelLease(existingLeaseRef.current?.guid);
    };

    const handleEditClick = useCallback(
        async (onSuccess?: () => void) => {
            if (!reportGuid) return;

            const durationInMs = getLeaseDuration();

            obtainEntityLeaseMutation
                .mutateAsync({
                    entityTypeId: EntityTypes.Report,
                    entityGuid: reportGuid!,
                    durationInMs,
                })
                .then((result) => {
                    if (result?.success) {
                        setDisplayMode(CollabReportDisplayMode.Edit);
                        setExistingLease(result.currentLease);

                        if (typeof onSuccess === 'function') {
                            onSuccess();
                        }
                    } else {
                        if (result?.currentLease) {
                            showErrorToast(
                                RenderFailedToObtainLeaseNotification(result?.currentLease)
                            );
                        }
                    }
                })
                .catch(() => {
                    showErrorToast(
                        `Failed to obtain lease for report - ${collaborationReport?.baseDetails.name}`
                    );
                });
        },
        [getLeaseDuration, modal, obtainEntityLeaseMutation, reportGuid]
    );

    const handleReportViewEventTriggered = useCallback(
        (event: string, data?: any) => {
            switch (event) {
                case 'commentsModalStateChange':
                    props.onShowCommentsModal(data.open, data.section);
                    return;
                case 'inputModelChange':
                    props.onInputModelChange(data.inputModel);
                    return;
            }
        },
        [props]
    );

    const handleContinueAsReadonlyClick = useCallback(() => {
        setDisplayMode(CollabReportDisplayMode.Readonly);
        setForceHideEditButton(true);
        modal.closeModal(CollabModalTypes.AlreadyEditing);
    }, [modal]);

    const handleContinueInEditModeClick = useCallback(() => {
        handleEditClick(() => {
            modal.closeModal(CollabModalTypes.AlreadyEditing);
        });
    }, [handleEditClick, modal]);

    const handleCancelClick = () => {
        setValidation({});

        if (hasUnsavedChanges) {
            modal.openModal<{ reason: CollabReportUnsavedChangesReason }, unknown>(
                CollabModalTypes.UnsavedChangesDetected,
                undefined,
                () => {
                    loadCollabReport();
                }
            );
        } else {
            setDisplayMode(CollabReportDisplayMode.Readonly);
        }

        if (existingLeaseRef.current?.guid) {
            cancelLease(false);
        }
    };

    const handleCloseChatActs = () => {
        if (collaborationReport) {
            const collaborationReportCopy = cloneDeep(collaborationReport);
            collaborationReportCopy.baseDetails.loggedInUserHasOpenChatAct = false;
            setCollaborationReport(collaborationReportCopy);
        }
    };

    const handleCollabActClosed = () => {
        if (collaborationReport) {
            const collaborationReportCopy = cloneDeep(collaborationReport);
            collaborationReportCopy.baseDetails.loggedInUserHasOpenCollabAct = false;
            setCollaborationReport(collaborationReportCopy);
        }
    };

    const handleFailedObtainedLease = (currentLease: IEntityLease) => {
        showErrorToast(RenderFailedToObtainLeaseNotification(currentLease));
    };

    const handleChatClick = () => {
        const actionTargets =
            zoneUsers.data
                ?.filter(
                    (reportUser) =>
                        collaborationReport?.collaborators
                            ?.map((collaborator) => collaborator.userGuid)
                            .includes(reportUser.guid) ||
                        collaborationReport?.readers
                            ?.map((reader) => reader.userGuid)
                            .includes(reportUser.guid)
                )
                .filter((reportUser) => reportUser.guid !== user.guid) ?? [];

        const modalData = {
            alertGuid: collaborationReport?.baseDetails.alertGuid ?? '',
            showCloseChatActButton: collaborationReport?.baseDetails.loggedInUserHasOpenChatAct,
            onCloseChatActs: handleCloseChatActs,
            actionTargets,
            reportGuid: reportGuid ?? '',
            reportName: collaborationReport?.baseDetails.name ?? '',
            user,
        };

        modal.openModal<IChatModalData, () => void>(CollabModalTypes.Chat, modalData);
    };

    const handleCloseReviewActClick = async () => {
        if (!collaborationReport?.baseDetails.alertGuid) {
            setValidation((prev) => ({ ...prev, model: ['Failed to close Review Act'] }));
        }

        const model = {
            alertGuid: collaborationReport?.baseDetails.alertGuid,
            alertType: AlertTypesEnum.Act,
            alertSubType: AlertSubTypesEnum.Review,
            parentEntityGuid: reportGuid,
            parentEntityType: EntityTypes.Report,
        } as IUpdateAlertUserModel;

        loading.showLoading();

        await closeChatActMutation
            .mutateAsync(model)
            .then(() => {
                if (!collaborationReport) return;
                const collaborationReportCopy = cloneDeep(collaborationReport);
                collaborationReportCopy.baseDetails.loggedInUserHasOpenReviewAct = false;
                setCollaborationReport(collaborationReportCopy);
            })
            .catch(() => {
                showErrorToast(
                    `Failed to close Review Act for report - ${collaborationReport?.baseDetails.name}`
                );
            })
            .finally(() => {
                loading.hideLoading();
            });
    };

    const handleCommentsClick = () => {
        props.onShowCommentsModal(true, 0);
    };

    const handleExportError = () => {
        // todo: any error handling
    };

    const handleHistoryClick = () => {
        const data = { reportGuid, history: collaborationReport?.history ?? [] };
        modal.openModal(CollabModalTypes.History, data);
    };

    const handlePreviewClick = () => {
        const newMode = isPreviewMode
            ? CollabReportDisplayMode.Readonly
            : CollabReportDisplayMode.Preview;

        setDisplayMode(newMode);
    };

    const handleRefreshClick = () => {
        props.getReportData?.();
    };

    const handleSaveAsClick = () => {
        const handleSaveAsModalClose = (modalHasOwnLease: boolean) => {
            setForceHideEditButton(false);
            cancelLease(!modalHasOwnLease);
            modal.closeModal(CollabModalTypes.Save);
        };

        const modalData: ISaveReportModalData = {
            alertGuid: collaborationReport?.baseDetails.alertGuid ?? '',
            availableUsers: CheckmateSelectHelper.getUserOptions(zoneUsers.data ?? []),
            closeButtonText: displayMode === CollabReportDisplayMode.Create ? 'Cancel' : 'Close',
            disableReaders: userIsReader,
            executeSaveReport: saveReport,
            leaseDuration: getLeaseDuration(),
            onClose: handleSaveAsModalClose,
            onCollabActClosed: handleCollabActClosed,
            onFailedToObtainLease: handleFailedObtainedLease,
            onLeaseObtained: (lease) => {
                setExistingLease(lease);
                setForceHideEditButton(true);
            },
            showEditButton: !existingLease && !reportIsPublished,
            readonly: saveModalIsReadonly,
            report: collaborationReport?.baseDetails,
            reportCollaborators:
                collaborationReport?.collaborators ??
                (reportGuid
                    ? []
                    : [
                          {
                              reportGuid,
                              reportUserTypeId: ReportUserTypesEnum.Collaborator,
                              userGuid: user.guid,
                          } as IReportUserModel,
                      ]),
            reportReaders: collaborationReport?.readers ?? [],
            title: reportGuid ? 'Collab' : 'Save Report',
            userStatuses: collaborationReport?.status,
        };

        modal.openModal<ISaveReportModalData, () => void>(CollabModalTypes.Save, modalData);
    };

    const handleSaveClick = async () => {
        setValidation({});

        const hasZeroSections = (props.inputModel.reportSectionDetails?.length ?? 0) === 0;

        if (
            hasZeroSections &&
            doesCollabReportRequireAtleast1Section(collaborationReport?.baseDetails?.type?.id ?? 0)
        ) {
            setValidation((prev) => ({ ...prev, settings: ['Select at least 1 section'] }));
            return;
        }

        const executeSaveReport = () => {
            if (collaborationReport) {
                saveReport(
                    collaborationReport.baseDetails,
                    collaborationReport.collaborators ?? [],
                    [],
                    false
                )
                    .then((payload) => {
                        if (payload) {
                            setCollaborationReport(payload);
                            setOriginalReportState({
                                collaborationReport: payload,
                                inputModel: props.inputModel,
                            });
                            setDisplayMode(CollabReportDisplayMode.Readonly);
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        setValidation((prev) => ({ ...prev, model: [error] }));
                    });
            }
        };

        if (!collabState.suppressSaveNotice) {
            const config: ISaveReportNoticeModalData = {
                onCancelClick: () => {
                    modal.closeModal(CollabModalTypes.SaveNotice);
                },
                onSaveClick: (suppressSaveNotice: boolean) => {
                    modal.closeModal(CollabModalTypes.SaveNotice);
                    setCollabState((prev) => ({ ...prev, suppressSaveNotice }));
                    executeSaveReport();
                },
            };

            modal.openModal(CollabModalTypes.SaveNotice, config);
        } else {
            executeSaveReport();
        }
    };

    const handleSectionCheckedChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections
    ) => {
        const newSectionDetails = getSectionsAfterSectionCheckedChange(
            event,
            section,
            props.inputModel.reportSectionDetails ?? []
        );

        props.onInputModelChange({
            ...props.inputModel,
            reportSectionDetails: newSectionDetails,
        });
    };

    const handleSettingsClick = () => {
        props.onShowSettingsModal(true);
    };

    const handleForceReleaseConfirm = useCallback(() => {
        if (!existingLease) return;
        const handleForceReleaseSuccess = () => {
            modal.closeModal(CollabModalTypes.ForceReleaseConfirm);
            modal.openModal(CollabModalTypes.ForceReleaseSuccess, undefined);
        };
        const handleForceReleaseError = () => {
            modal.closeModal(CollabModalTypes.ForceReleaseConfirm);
        };
        forceCancelLease(existingLease.guid, handleForceReleaseSuccess, handleForceReleaseError);
    }, [existingLease, forceCancelLease, modal]);

    const handleForceReleaseClick = useCallback(() => {
        if (!existingLease) return;
        modal.openModal(CollabModalTypes.ForceReleaseConfirm, undefined, handleForceReleaseConfirm);
    }, [existingLease, handleForceReleaseConfirm, modal]);

    const closeNote = () => {
        const validationCopy = cloneDeep(validation);
        validationCopy.categories = [];
        validationCopy.content = [];
        setCollabState((prev) => ({ ...prev, currentNote: undefined }));
        setValidation(validationCopy);
    };

    const handleSaveNoteComplete = () => {
        // Per CHECK-3392, no longer refreshing the report data after saving a note.
        setRefreshDataDialogState((prev) => ({ ...prev, show: true }));
        closeNote();
    };

    const handleCancelAddEditNote = () => {
        closeNote();
    };

    const bindSignalrEvents = useCallback(() => {
        const handleSendNewOpenChatActReceived = (model: IOpenChatActModel) => {
            // console.log(`******* ${SignalRMethods.SendOpenChatActStatus} *******`, model);

            if (model.entityGuid === reportGuid && collaborationReport) {
                const collaborationReportCopy = cloneDeep(collaborationReport);
                collaborationReportCopy.baseDetails.loggedInUserHasOpenChatAct =
                    model.loggedInUserHasOpenChatAct;
                setCollaborationReport(collaborationReportCopy);
            }
        };

        if (alertHub !== undefined) {
            alertHub?.on(SignalRMethods.SendOpenChatActStatus, handleSendNewOpenChatActReceived);
            console.debug(
                `SignalR - Alert Hub => Listening for ${SignalRMethods.SendOpenChatActStatus}`
            );
        }
    }, [alertHub, collaborationReport, reportGuid]);

    const unbindSignalrEvents = useCallback(() => {
        alertHub?.off(SignalRMethods.SendOpenChatActStatus);
        console.debug(
            `SignalR - Alert Hub => Stopped listening for ${SignalRMethods.SendOpenChatActStatus}`
        );
    }, [alertHub]);

    const easyUpdateDone = async (updatedCase: ICaseModel) => {
        setRefreshDataDialogState((prev) => ({ ...prev, show: true }));
        setCollabState((prev) => ({
            ...prev,
            easyUpdateMode: false,
            easyUpdateCaseGuid: undefined,
        }));
    };

    const getNoteCategoryTypesByNoteType = () => {
        const noteTypeId = collabState.currentNote?.type ? collabState.currentNote.type.id : 0;
        return noteCategoryTypes.data?.filter((x) => x.noteType.id === noteTypeId) ?? [];
    };

    const handleDismissSubmitClick = (suppress: boolean) => {
        setRefreshDataDialogState({ show: false, hasBeenDismissed: suppress });
        props.getReportData?.();
    };

    const handleDismissCloseClick = (suppress: boolean) => {
        setRefreshDataDialogState({ show: false, hasBeenDismissed: suppress });
    };

    useEffect(() => {
        if (reportGuid) {
            loadCollabReport();
        }
    }, [reportGuid]);

    useEffect(() => {
        modal.patchModalData(CollabModalTypes.Chat, {
            showCloseChatActButton: collaborationReport?.baseDetails.loggedInUserHasOpenChatAct,
        });
    }, [collaborationReport?.baseDetails.loggedInUserHasOpenChatAct, modal]);

    useEffect(() => {
        onReportStatusChange(
            collaborationReport?.baseDetails.status ?? { id: ReportStatusTypesEnum.Draft }
        );
    }, [onReportStatusChange, collaborationReport?.baseDetails.status]);

    useEffect(() => {
        if (!!reportGuid && !!navigationType) {
            getIsEntityLeaseAvailable.refetch().then((response) => {
                const lease = response.data;
                const lesseeUser = lease?.lesseeUser;
                const lesseeProfile = lesseeUser?.profile;
                const leaseDialogBody = `This report is currently being edited by ${lesseeProfile?.firstName} ${lesseeProfile?.lastName}.\nThe report will be opened in read-only mode.`;
                if (response.isError) {
                    showErrorToast(<span>{leaseDialogBody}</span>);
                    setExistingLease(undefined);
                } else if (lease) {
                    setExistingLease(lease);
                    const lesseeIsCurrentUser = lease?.lesseeUser?.guid !== user.guid;
                    if (lesseeIsCurrentUser) {
                        showInfoToast(<span>{leaseDialogBody}</span>);
                    } else {
                        if (
                            navigationType === EntryTypeEnum.navigate ||
                            navigationType === EntryTypeEnum.reload
                        ) {
                            modal.openModal<IAlreadyEditingModalData, () => void>(
                                CollabModalTypes.AlreadyEditing,
                                {
                                    onEditButtonClick: handleContinueInEditModeClick,
                                    onReadOnlyClick: handleContinueAsReadonlyClick,
                                    leftFooterComponent: (
                                        <Stack direction="horizontal" gap={1}>
                                            {!!existingLeaseRef.current && (
                                                <LoadingButton
                                                    loading={releaseEntityLeaseMutation.isLoading}
                                                    loadingText="Releasing..."
                                                    onClick={handleForceReleaseClick}
                                                    text="Force Release"
                                                    variant="default"
                                                />
                                            )}
                                        </Stack>
                                    ),
                                }
                            );
                        }
                    }
                    setForceHideEditButton(true);
                } else {
                    modal.closeModal(CollabModalTypes.ExistingLease);
                    setForceHideEditButton(false);
                }
            });
        }
    }, [navigationType, reportGuid, user.guid]);

    useEffect(() => {
        existingLeaseRef.current = existingLease;
    }, [JSON.stringify(existingLease)]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        const pingMs = getLeasePingFrequency();
        (function loop() {
            if (
                existingLeaseRef.current?.guid &&
                reportGuid &&
                displayMode === CollabReportDisplayMode.Edit
            ) {
                timeoutId = setTimeout(() => {
                    pingMutation.mutateAsync(existingLeaseRef.current!.guid);
                    loop();
                }, pingMs);
            }
        })();
        return () => clearTimeout(timeoutId);
    }, [
        existingLeaseRef.current?.guid,
        displayMode,
        getLeasePingFrequency,
        reportGuid,
        pingMutation,
    ]);

    useEffect(() => {
        if (collaborationReport && alertHub) {
            bindSignalrEvents();
        }
        return unbindSignalrEvents;
    }, [bindSignalrEvents, unbindSignalrEvents, alertHub, collaborationReport]);

    // Prevent reader from viewing a non-Published report
    useEffect(() => {
        if (isLoading || !collaborationReport || !user) return;

        const { readers = [], baseDetails } = collaborationReport;
        const isReaderUser = readers.some((reader) => reader.userGuid === user.guid);
        const isPublished = baseDetails?.status?.id === ReportStatusTypesEnum.Published;

        if (isReaderUser && !isPublished) {
            console.warn('User is a Reader and may not view a non-Published report.');
            zoneContext.zoneNavigate(LocalRoutes.AccessDenied, { replace: true });
        }
    }, [isLoading, collaborationReport, user, zoneContext]);

    const userIsReader = !!collaborationReport?.readers?.some(
        (reader) => reader.userGuid === user.guid
    );
    const reportStatusId = collaborationReport?.baseDetails.status?.id;
    const reportIsDraftStatus = reportStatusId === ReportStatusTypesEnum.Draft;
    const reportIsPublished = reportStatusId === ReportStatusTypesEnum.Published;
    const reportIsFinalDraftStatus = reportStatusId === ReportStatusTypesEnum.FinalDraft;

    const enableCollab =
        appSettings.data?.find(
            (setting) => setting.name === DatabaseAppSettingsEnum.DisableCollabReportFeature
        )?.value !== 'true' && !appSettings.isLoading;

    const isPreviewMode = displayMode === CollabReportDisplayMode.Preview || reportIsPublished;
    const isReadonlyMode = displayMode === CollabReportDisplayMode.Readonly || isPreviewMode;

    const lastModifiedByCurrentUser =
        collaborationReport?.baseDetails.modifiedBy?.guid === user.guid;

    const saveModalIsReadonly =
        isReadonlyMode || (!!reportGuid && !reportIsDraftStatus && !lastModifiedByCurrentUser);

    let ReportRenderer;

    switch (props.reportTypeId) {
        case SystemGeneratedDocumentsTypesEnum.WeeklyRecap.Value:
            ReportRenderer = WeeklyRecapReportRenderer<
                IWeeklyRecapModel,
                ITimelineReportInputModel
            >;
            break;
        case SystemGeneratedDocumentsTypesEnum.MonthlyRecap.Value:
            ReportRenderer = MonthlyRecapReportRenderer<
                IMonthlyRecapModel,
                ITimelineReportInputModel
            >;
            break;
        case SystemGeneratedDocumentsTypesEnum.KpiReport.Value:
            ReportRenderer = KpiReportRenderer<IKpiReportModel, IKpiReportInputModel>;
            break;
        default:
            ReportRenderer = () => <div>No report renderer found for {props.reportTypeId}</div>;
    }

    const showLoader =
        isLoading ||
        isSaving ||
        orgSettings.isFetching ||
        zoneUsers.isFetching ||
        noteCategoryTypes.isFetching ||
        getIsEntityLeaseAvailable.isFetching ||
        appSettings.isFetching;

    const showNoteEditor = collabState.currentNote !== undefined;

    const getDocumentTypeId = () => {
        return 999;
    };
    
    const getDocumentSubTypeId = () => {
        return 999;
    };

    const documentTypeId = getDocumentTypeId();

    const documentSubTypeId = getDocumentSubTypeId();

    return (
        <>
            <UnsavedChangesBlocker when={hasUnsavedChanges} />
            <DocumentTitle title={props.pageTitle} />

            {Authorization.userHasRight(UserRightsEnum.EasyUpdate, user, zoneContext.zoneGuid) && (
                <CaseEasyUpdate
                    caseGuid={collabState.easyUpdateCaseGuid}
                    onComplete={easyUpdateDone}
                    onCancel={() => {
                        setCollabState((prev) => ({
                            ...prev,
                            easyUpdateMode: false,
                            easyUpdateCaseGuid: undefined,
                        }));
                    }}
                    hidden={!collabState.easyUpdateMode}
                />
            )}

            <DismissableModal
                title="Refresh Data"
                bodyComponent={
                    <p>
                        Case edits you've made during this session are not reflected in this view
                        until you refresh.
                    </p>
                }
                submitButton={{
                    cssClass: 'btn-orange',
                    onClick: handleDismissSubmitClick,
                    label: 'Refresh',
                }}
                closeButton={{
                    onClick: handleDismissCloseClick,
                    label: 'Dismiss',
                }}
                open={refreshDataDialogState.show && !refreshDataDialogState.hasBeenDismissed}
            />

            {showNoteEditor && collabState.currentNote && (
                <MainContentOverlayComponent>
                    <NoteEditor
                        caseTitle={NoteHelper.getCaseTitle(collabState.currentNote)}
                        noteCategoryTypes={getNoteCategoryTypesByNoteType()}
                        currentNote={collabState.currentNote}
                        caseLocalCounselGuid={collabState.currentNote.localCounselOrganizationGuid}
                        defaultAlertTarget={
                            Authorization.isLocalScope(user, zoneContext.zoneGuid)
                                ? collabState.currentNote.caseManager
                                : undefined
                        }
                        defaultWatchTargets={
                            Authorization.isLocalScope(user, zoneContext.zoneGuid) &&
                            collabState.currentNote.alternateCaseManager
                                ? [collabState.currentNote.alternateCaseManager]
                                : undefined
                        }
                        onSaveComplete={handleSaveNoteComplete}
                        onCancel={handleCancelAddEditNote}
                    />
                </MainContentOverlayComponent>
            )}

            {!collabState.easyUpdateMode && (
                <PageWithAffixedHeader
                    printHide={showNoteEditor}
                    headerComponent={
                        <>
                            <CollabReportPageHeader
                                title={props.pageTitle}
                                actionsComponent={
                                    <CollabReportPageActions
                                        enableCollab={enableCollab}
                                        exportSettings={props.exportSettings}
                                        forceHideEditButton={forceHideEditButton}
                                        hasChatActs={
                                            collaborationReport?.baseDetails
                                                .loggedInUserHasOpenChatAct
                                        }
                                        hasReviewAct={
                                            collaborationReport?.baseDetails
                                                .loggedInUserHasOpenReviewAct
                                        }
                                        hasCollabAct={
                                            collaborationReport?.baseDetails
                                                .loggedInUserHasOpenCollabAct
                                        }
                                        hasUnsavedChanges={hasUnsavedChanges}
                                        isClosingReviewAct={closeChatActMutation.isLoading}
                                        isPreviewMode={isPreviewMode}
                                        onCancelClick={handleCancelClick}
                                        onChatClick={handleChatClick}
                                        onCloseReviewActClick={handleCloseReviewActClick}
                                        onCommentsClick={handleCommentsClick}
                                        onEditClick={handleEditClick}
                                        onExportError={handleExportError}
                                        onHistoryClick={handleHistoryClick}
                                        onPreviewClick={handlePreviewClick}
                                        onRefreshClick={handleRefreshClick}
                                        onSaveAsClick={handleSaveAsClick}
                                        onSaveClick={handleSaveClick}
                                        onSettingsClick={handleSettingsClick}
                                        pageActionsDisplayType={getPageActionsDisplayType()}
                                        reportStatus={collaborationReport?.baseDetails.status}
                                        reportTypeId={props.reportTypeId}
                                        userIsReader={userIsReader}
                                    />
                                }
                                inputModel={props.inputModel}
                                isCollabReport={!!reportGuid}
                                isInADraftStatus={reportIsDraftStatus || reportIsFinalDraftStatus}
                                report={collaborationReport?.baseDetails}
                            />
                            <PageErrors validation={validation} />
                        </>
                    }
                >
                    {showLoader ? (
                        <Loader />
                    ) : (
                        <ReportRenderer
                            collabReportState={collabState}
                            data={props.data as any}
                            inputModel={props.inputModel as any}
                            isPreviewMode={isPreviewMode}
                            isReportReadonly={isReadonlyMode}
                            onEventTriggered={handleReportViewEventTriggered}
                            onSectionCheckedChange={handleSectionCheckedChange}
                            reportGuid={reportGuid}
                            setCollabState={setCollabState}
                            title={props.pageTitle}
                        />
                    )}
                </PageWithAffixedHeader>
            )}
        </>
    );
}

export default CollabReportController;

function PageErrors(props: { validation: IValidation }) {
    return (
        <>
            {props.validation.model && (
                <div>
                    <span className="text-danger">{props.validation.model}</span>
                </div>
            )}
            {props.validation.settings && (
                <div>
                    <span className="text-danger">{props.validation.settings}</span>
                </div>
            )}
        </>
    );
}
